import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import socioService from "../services/socioService.tsx";
import { asignarCupon, desasignarCupon } from "../services/cuponService.tsx";
import { Socio } from "../types/Socio.tsx";

type SocioAsignado = {
  socio_id?: string | number;
  socio_nombre?: string;
  socio_dni?: string;
};

const AsignarCuponModal: React.FC<{
  cuponId: number;
  sociosAsignados?: SocioAsignado[];
  onAsignado?: (socio: SocioAsignado) => void;
  onDesasignado?: (socioId: string | number) => void;
  onClose: () => void;
}> = ({ cuponId, sociosAsignados = [], onAsignado, onDesasignado, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [listaSocios, setListaSocios] = useState<Socio[]>([]);
  const [searching, setSearching] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [unassigningId, setUnassigningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearching(true);
    setError(null);
    setExito(null);
    setListaSocios([]);

    if (!searchTerm.trim()) {
      setError("El campo de búsqueda no puede estar vacío.");
      setSearching(false);
      return;
    }

    try {
      const data = await socioService.searchSocios(
        searchTerm.trim(),
        getAccessTokenSilently
      );

      if (!data?.length) {
        setError("No se encontraron socios que coincidan con la búsqueda.");
      } else {
        setListaSocios(data);
      }
    } catch (err) {
      console.error("Error completo:", err);
      setError("No se pudieron cargar los socios. Intenta nuevamente.");
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async (socio: Socio) => {
    setAssigningId(socio.socio_id);
    setExito(null);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const result = await asignarCupon(
        Number(socio.socio_id),
        Number(cuponId),
        token
      );
      const yaAsignado = Boolean(result?.alreadyAssigned);
      setExito(
        yaAsignado
          ? `El cupón ya estaba asignado a ${socio.socio_nombre}.`
          : `Cupón asignado a ${socio.socio_nombre}.`
      );
      onAsignado?.({
        socio_id: socio.socio_id,
        socio_nombre: socio.socio_nombre,
        socio_dni: socio.socio_dni,
      });
    } catch (err: any) {
      console.error("Error al asignar cupón:", err);
      const apiMsg =
        err?.response?.data?.errorDetails?.message ||
        err?.response?.data?.message ||
        err?.message;
      setError(apiMsg || "No se pudo asignar el cupón. Intenta nuevamente.");
    } finally {
      setAssigningId(null);
    }
  };

  const handleUnassign = async (socioId: string | number) => {
    setUnassigningId(String(socioId));
    setError(null);
    setExito(null);
    try {
      const token = await getAccessTokenSilently();
      await desasignarCupon(Number(socioId), Number(cuponId), token);
      setExito("Cupón desasignado correctamente.");
      onDesasignado?.(socioId);
    } catch (err: any) {
      console.error("Error al desasignar cupón:", err);
      const apiMsg =
        err?.response?.data?.errorDetails?.message ||
        err?.response?.data?.message ||
        err?.message;
      setError(
        apiMsg || "No se pudo desasignar el cupón. Intenta nuevamente."
      );
    } finally {
      setUnassigningId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur">
      <div className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="grid gap-6 lg:grid-cols-[1fr,1.1fr]">
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900">
              Asignar Cupón a un Socio
            </h2>

            {sociosAsignados.length > 0 && (
              <section className="rounded-xl bg-gray-50 p-3">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Ya asignado a
                </p>
                <div className="space-y-2">
                  {sociosAsignados.map((socio, idx) => (
                    <div
                      key={`${socio.socio_id}-${idx}`}
                      className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs text-gray-700 shadow-sm"
                    >
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-800">
                          {socio.socio_nombre || socio.socio_id}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {socio.socio_dni ? `DNI ${socio.socio_dni}` : "Sin DNI"}
                        </span>
                      </div>
                      <button
                        onClick={() => handleUnassign(socio.socio_id || "")}
                        disabled={!!unassigningId}
                        className="rounded-lg bg-red-50 px-3 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:bg-red-50 disabled:text-red-300"
                      >
                        {unassigningId === String(socio.socio_id)
                          ? "Desasignando..."
                          : "Desasignar"}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
              <p className="text-sm font-semibold text-gray-800 mb-2">
                Buscar socio
              </p>
              <form onSubmit={handleSearch} className="space-y-2">
                <input
                  type="text"
                  placeholder="Por nombre o ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                <button
                  type="submit"
                  disabled={searching}
                  className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                >
                  {searching ? "Buscando..." : "Buscar"}
                </button>
              </form>
            </section>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            {exito && (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {exito}
              </p>
            )}

            <button
              onClick={onClose}
              className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
            >
              Cerrar
            </button>
          </div>

          <div className="flex flex-col">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Resultados</p>
              <span className="text-xs text-gray-500">
                {listaSocios.length} encontrado(s)
              </span>
            </div>
            <div className="flex-1 max-h-[520px] overflow-y-auto rounded-xl border border-gray-100 bg-white/70 p-3">
              <ul className="grid gap-3 sm:grid-cols-1">
                {listaSocios.map((socio) => (
                  <li
                    key={socio.socio_id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="pr-0 sm:pr-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {socio.socio_nombre || "Sin nombre"}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {socio.socio_id} · DNI: {socio.socio_dni || "—"}
                      </p>
                      {Array.isArray(
                        (socio as any)?.cuponesAsignados ??
                          (socio as any)?.cupones
                      ) &&
                        ((socio as any)?.cuponesAsignados ??
                          (socio as any)?.cupones).some(
                          (c: any) =>
                            Number(c?.cuponId ?? c?.id ?? c?.cupon_id) ===
                            Number(cuponId)
                        ) && (
                          <p className="text-xs font-semibold text-amber-700">
                            Ya tiene este cupón asignado.
                          </p>
                        )}
                    </div>
                    {(() => {
                      const asignados =
                        (socio as any)?.cuponesAsignados ??
                        (socio as any)?.cupones;
                      const yaAsignado =
                        Array.isArray(asignados) &&
                        asignados.some(
                          (c: any) =>
                            Number(c?.cuponId ?? c?.id ?? c?.cupon_id) ===
                            Number(cuponId)
                        );
                      return (
                        <button
                          onClick={() => handleAssign(socio)}
                          disabled={!!assigningId || yaAsignado}
                          className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 sm:w-auto"
                        >
                          {yaAsignado
                            ? "Ya asignado"
                            : assigningId === socio.socio_id
                            ? "Asignando..."
                            : "Asignar"}
                        </button>
                      );
                    })()}
                  </li>
                ))}
                {listaSocios.length === 0 && !searching && (
                  <li className="col-span-full rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                    Iniciá una búsqueda para ver resultados.
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsignarCuponModal;
